import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '@api/api';

export type AnalyticsPeriod = '24h' | '7d' | '30d' | '90d';

export interface AnalyticsSummary {
  totalBundles: number;
  totalChannels: number;
  totalInstalls: number;
  newDevices: number;
  storageUsedBytes: number;
}

export interface TimeSeriesPoint {
  date: string;
  count: number;
}

export interface PlatformSplitEntry {
  platform: string;
  count: number;
}

export interface ChannelActivityEntry {
  channelId: string;
  channelName: string;
  bundleCount: number;
}

export interface RolloutStatusEntry {
  channelId: string;
  channelName: string;
  hash: string;
  rolloutPct: number;
  status: string;
  installedCount: number;
  totalInstalls: number;
  adoptionPct: number;
}

export interface RuntimeVersionEntry {
  runtimeVersion: string;
  count: number;
}

export interface ActiveDevicesPerChannelEntry {
  channelId: string;
  channelName: string;
  count: number;
}

export interface AppEnvironmentDistributionEntry {
  environment: string;
  count: number;
}

export interface GeoSummary {
  totalActiveInstalls: number;
  trackedActiveInstalls: number;
  untrackedActiveInstalls: number;
}

export interface CountryDistributionEntry {
  countryCode: string;
  count: number;
}

export interface RegionDistributionEntry {
  countryCode: string;
  region: string;
  regionCode: string | null;
  count: number;
}

export interface CityDistributionEntry {
  countryCode: string;
  region: string | null;
  regionCode: string | null;
  city: string;
  latitude: number;
  longitude: number;
  count: number;
}

export interface BundleReleaseFilter {
  hash: string;
  label: string;
  version?: string;
  uploadedAt?: string;
}

export interface AvailableFilters {
  channels: { channelId: string; name: string }[];
  platforms: string[];
  environments: string[];
  runtimeVersions: string[];
  countries: string[];
  bundleReleases: BundleReleaseFilter[];
}

export interface ProjectAnalyticsResponse {
  summary: AnalyticsSummary;
  bundleUploadsOverTime: TimeSeriesPoint[];
  activeInstallsOverTime: TimeSeriesPoint[];
  platformSplit: PlatformSplitEntry[];
  channelActivity: ChannelActivityEntry[];
  rolloutStatus: RolloutStatusEntry[];
  runtimeVersionDistribution: RuntimeVersionEntry[];
  activeDevicesPerChannel: ActiveDevicesPerChannelEntry[];
  environmentDistribution: AppEnvironmentDistributionEntry[];
  geoSummary: GeoSummary;
  countryDistribution: CountryDistributionEntry[];
  regionDistribution: RegionDistributionEntry[];
  cityDistribution: CityDistributionEntry[];
  availableFilters: AvailableFilters;
}

export interface ProjectAnalyticsRequest {
  projectId: string;
  period?: AnalyticsPeriod;
  channelId?: string;
  platform?: string;
  appEnvironment?: string;
  runtimeVersion?: string;
  bundleHash?: string;
  country?: string;
}

export const analyticsApi = createApi({
  reducerPath: 'analyticsApi',
  baseQuery: axiosBaseQuery(),
  endpoints: builder => ({
    getProjectAnalytics: builder.query<ProjectAnalyticsResponse, ProjectAnalyticsRequest>({
      query: ({
        projectId,
        period = '30d',
        channelId,
        platform,
        appEnvironment,
        runtimeVersion,
        bundleHash,
        country,
      }) => {
        const params: Record<string, string> = { period };
        if (channelId) params.channelId = channelId;
        if (platform) params.platform = platform;
        if (appEnvironment) params.environment = appEnvironment;
        if (runtimeVersion) params.runtimeVersion = runtimeVersion;
        if (bundleHash) params.bundleHash = bundleHash;
        if (country) params.country = country;
        return {
          url: `/projects/${projectId}/analytics`,
          method: 'GET',
          params,
        };
      },
    }),
  }),
});

export const { useGetProjectAnalyticsQuery } = analyticsApi;
